<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <span class="w-full flex justify-center" @click="$router.push('/')">
        <LogoText />
      </span>
      <h2 class="mt-6 text-center text-3xl font-semibold text-gray-600">
        {{ $t("Reset you password") }}
      </h2>
    </div>
    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
      <div class="bg-white py-8 px-4 pt-2 shadow sm:rounded-lg sm:px-10">
        <Form :validation-schema="schema" class="mt-2 space-y-5 p-3" @submit="onSubmit">
          <div class="rounded-md grid grid-cols-1 shadow-sm space-y-2 gap-2">
            <TextInput
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="password"
              type="password"
              :label="$t('New password')"
              :placeholder="$t('Your new password')" />

            <TextInput
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="re-password"
              type="password"
              :label="$t('Confirm password')"
              :placeholder="$t('Re-Type Your password')" />
          </div>
          <div class="flex items-center justify-between" />

          <div>
            <button
              type="submit"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light">
              <span class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-blue group-hover:text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </span>
              {{ $t("Reset") }}
            </button>
          </div>
        </Form>
      </div>
      <TransitionRoot as="template" :show="expired_link_error">
        <Dialog as="div"
                static
                class="fixed z-10 inset-0 overflow-y-auto"
                :open="expired_link_error"
                @close="expired_link_error = false">
          <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in duration-200"
              leave-from="opacity-100"
              leave-to="opacity-0">
              <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>
            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <div
                class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div class="sm:flex sm:items-start">
                    <div
                      class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <TriangleAlert class="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
                        {{ $t("Expired link") }}
                      </DialogTitle>
                      <div class="mt-2">
                        <p class="text-sm text-gray-500">
                          {{ $t("The link you are using has expired, please try to reset you password again") }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    ref="cancelButtonRef"
                    type="button"
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    @click="expired_link_error = false">
                    {{ $t("Cancel") }}
                  </button>
                </div>
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/auth.service"
import * as Yup from "yup"
import { ref } from "vue"
import { Form } from "vee-validate"
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue"
import { TriangleAlert } from "lucide-vue-next"
import TextInput from "../components/shared/TextInput.vue"
import LogoText from "@/components/shared/LogoText.vue"
export default {
  name: "Login",

  components: {
    TextInput,
    LogoText,
    Form,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    TriangleAlert
  },
  setup() {
    const expired_link_error = ref(false)

    function equalTo(ref, msg) {
      // eslint-disable-next-line vue/no-undef-properties
      return this.test({
        name: "equalTo",
        exclusive: false,
        message: msg || "${path} must be the same as ${reference}",
        params: {
          reference: ref.path
        },
        test: function (value) {
          // eslint-disable-next-line vue/no-undef-properties
          return value === this.resolve(ref)
        }
      })
    }

    Yup.addMethod(Yup.string, "equalTo", equalTo)

    // Using yup to generate a validation schema
    // https://vee-validate.logaretm.com/v4/guide/validation#validation-schemas-with-yup
    const schema = Yup.object().shape({
      password: Yup.string().min(8).required(),
      "re-password": Yup.string().equalTo(Yup.ref("password"), "Please enter the same password")
    })

    return {
      schema,
      expired_link_error
    }
  },
  data() {
    return {
      uid: null,
      token: null
    }
  },

  created() {
    this.token = this.$route.params.token
    this.uid = this.$route.params.uid
  },

  methods: {
    onSubmit(values) {
      this.$store.dispatch("ui/showLoading")
      if (values.password && values["re-password"]) {
        AuthService.resetPassword(this.uid, this.token, values.password, values.password)
          .then(() => {
            this.$store.dispatch("ui/hideLoading")
            this.$router.replace("/login")
          })
          .catch(() => {
            this.$store.dispatch("ui/hideLoading")
            this.expired_link_error = true
          })
      }
    }
  }
}
</script>
